/**
 * Responsibility: build the outbound links on a card's detail view.
 *
 * All three targets are search URLs rather than deep links, and that is a
 * deliberate limitation rather than laziness.
 *
 * PSA's Auction Prices Realized is the best source of graded sale prices
 * there is - their own data, millions of results, updated daily - and it is
 * gated behind a Collectors account. Their per-card URLs also key on a PSA
 * spec id we do not hold, since our population data comes from GemRate by way
 * of BetterPop rather than from PSA directly. So this opens their search with
 * the card already typed in: one click and one login, rather than a link that
 * would 404 or bounce to a sign-in page with no query preserved.
 *
 * BetterPop is where the population figures on this page come from, so the
 * link is there to check the numbers at source. Their app filters client-side
 * from one dataset and does not route per card, hence a search too.
 */
import type { DiscoveredCard } from '../../api/client';

const EBAY_SEARCH = 'https://www.ebay.com/sch/i.html';

/** Trading cards. The same category the hub prices against. */
const EBAY_CATEGORY = '183454';

const PSA_APR = 'https://www.psacard.com/auctionprices';
const BETTERPOP = 'https://better-pop.com/app';

/**
 * The card as a person would search for it.
 *
 * The name is cut at the first hyphen because BetterPop suffixes the
 * printing onto it - "Aggron-Reverse Foil" - and no marketplace search
 * recognises that as part of the card's name.
 */
export function searchTerms(card: DiscoveredCard, suffix = ''): string {
  return [card.card_name.split('-')[0]?.trim(), card.card_number, card.set_name, suffix]
    .filter(Boolean)
    .join(' ');
}

/** eBay completed sales for this card at an optional grade. */
export function ebaySoldUrl(card: DiscoveredCard, suffix = ''): string {
  const params = new URLSearchParams({
    _nkw: searchTerms(card, suffix),
    _sacat: EBAY_CATEGORY,
    LH_Complete: '1',
    LH_Sold: '1',
  });
  return `${EBAY_SEARCH}?${params.toString()}`;
}

/** PSA's Auction Prices Realized, pre-searched. Needs a PSA login. */
export function psaAprUrl(card: DiscoveredCard): string {
  const params = new URLSearchParams({ q: searchTerms(card) });
  return `${PSA_APR}/search?${params.toString()}`;
}

/** BetterPop, where the population figures came from. */
export function betterPopUrl(card: DiscoveredCard): string {
  const params = new URLSearchParams({ q: searchTerms(card) });
  return `${BETTERPOP}/?${params.toString()}`;
}
